import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { getFaturamentoStatusClasse } from '../utils/faturamentoStatusUtils'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { ClipboardList, Search, X } from 'lucide-react'
import { format } from 'date-fns'

import { useToast } from '../../../contexts/NotificationContext'
import { useGlobalLoading } from '../../../contexts/LoadingContext'
import { useUsuario } from '../../../contexts/UsuarioContext'
import { InputDate } from '../../../componentesCompartilhados/form'
import { zOptionalDate, zRequiredDate } from '../../../utils/validacao'
import { formatDateToBrazilian } from '../../../utils/formatUtils'
import { formatCurrency, formatNumber } from '../../../utils'
import { faturamentoService, type BuscarAcompanhamentoFaturamentoDto, type FaturamentoDetalhesDto, type FaturamentoDto, type FaturamentoStatusDto, type AnalistaSMNDto, type EtapaFaturamentoDto } from '../services/faturamento'
import { FaturamentoDetalhesConteudo } from '../componentes/FaturamentoDetalhesConteudo'
import { timeService } from '../../times/services/time'
import type { Time } from '../../times/tipos/time'

interface AcompanhamentoFilters {
  dataInicio: string
  dataFim?: string
  nomeProjeto?: string
  numeroTicket?: string
  statusCodigo?: string
  analistaSMNId?: string
  idTime?: string
}

/** Retorna início e fim da quinzena conforme data de processamento: 01 a 15 e 16 ao último dia do mês */
function getQuinzenaInicioFim(dataProcessamento: Date): { inicio: string; fim: string } {
  const ano = dataProcessamento.getFullYear()
  const mes = dataProcessamento.getMonth()
  const dia = dataProcessamento.getDate()
  let inicio: Date
  let fim: Date
  if (dia <= 15) {
    inicio = new Date(ano, mes, 1)
    fim = new Date(ano, mes, 15)
  } else {
    inicio = new Date(ano, mes, 16)
    fim = new Date(ano, mes + 1, 0)
  }
  return {
    inicio: format(inicio, 'yyyy-MM-dd'),
    fim: format(fim, 'yyyy-MM-dd'),
  }
}

const acompanhamentoSchema = z.object({
  dataInicio: zRequiredDate,
  dataFim: zOptionalDate,
  nomeProjeto: z.string().optional(),
  numeroTicket: z.string().optional(),
  statusCodigo: z.string().optional(),
  analistaSMNId: z.string().optional(),
  idTime: z.string().optional(),
}).superRefine((data, ctx) => {
  if (data.dataFim && data.dataInicio && typeof data.dataFim === 'string' && typeof data.dataInicio === 'string') {
    const inicio = new Date(data.dataInicio)
    const fim = new Date(data.dataFim)

    if (!isNaN(inicio.getTime()) && !isNaN(fim.getTime()) && fim < inicio) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dataFim'],
        message: 'Data fim deve ser maior ou igual à data início',
      })
    }
  }
})

const getStatusBadgeClass = getFaturamentoStatusClasse

function formatHoras(horas: number | null | undefined): string {
  if (horas == null || Number.isNaN(horas)) return '-'
  return `${formatNumber(horas)} h`
}

/** Coluna Responsável: colaboradores do time (acompanhamento analista) ou SMN do ticket */
function responsavelAcompanhamentoExibicao(f: FaturamentoDto): string {
  const nomes = f.colaboradoresResumo?.map((c) => c.nome?.trim()).filter((n): n is string => Boolean(n)) ?? []
  if (nomes.length > 0)
    return [...new Set(nomes)].join(', ')
  return f.analistaSMNNome?.trim() || '-'
}

/** Linhas de suporte por lote (pré-faturamento) podem existir sem registro de Faturamento ainda (id 0). */
function detalhesFaturamentoDisponivel(f: FaturamentoDto): boolean {
  return f.idFaturamento > 0
}

export default function FaturamentoAcompanhamento() {
  const toast = useToast()
  const { startLoading, stopLoading } = useGlobalLoading()
  const { usuario } = useUsuario()
  const isAdmin = usuario?.papeis?.includes('1') ?? false

  const [statusFaturamento, setStatusFaturamento] = useState<FaturamentoStatusDto[]>([])
  const [etapasFaturamento, setEtapasFaturamento] = useState<EtapaFaturamentoDto[]>([])
  const [analistasSMN, setAnalistasSMN] = useState<AnalistaSMNDto[]>([])
  const [times, setTimes] = useState<Time[]>([])
  const [faturamentos, setFaturamentos] = useState<FaturamentoDto[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [buscaRealizada, setBuscaRealizada] = useState(false)
  const cargaInicialFeita = useRef(false)

  type PainelDetalhesState = {
    faturamentoLinha: FaturamentoDto
    detalhes: FaturamentoDetalhesDto | null
    faturamentosProjeto: FaturamentoDto[] | null
    carregando: boolean
    erro: string | null
  }

  const [painelDetalhes, setPainelDetalhes] = useState<PainelDetalhesState | null>(null)
  const cacheDetalhesRef = useRef(
    new Map<number, { detalhes: FaturamentoDetalhesDto; faturamentosProjeto: FaturamentoDto[] }>(),
  )

  const fecharPainelDetalhes = useCallback(() => {
    setPainelDetalhes(null)
  }, [])

  const abrirPainelDetalhes = useCallback(
    (f: FaturamentoDto) => {
      if (!detalhesFaturamentoDisponivel(f)) {
        toast.info(
          'Detalhes indisponíveis',
          'Este lançamento ainda não possui faturamento vinculado (ex.: lote de suporte aguardando processamento). Os detalhes completos aparecem após o faturamento ser gravado e vinculado.',
        )
        return
      }
      const cached = cacheDetalhesRef.current.get(f.idFaturamento)
      if (cached) {
        setPainelDetalhes({
          faturamentoLinha: f,
          detalhes: cached.detalhes,
          faturamentosProjeto: cached.faturamentosProjeto,
          carregando: false,
          erro: null,
        })
        return
      }
      setPainelDetalhes({
        faturamentoLinha: f,
        detalhes: null,
        faturamentosProjeto: null,
        carregando: true,
        erro: null,
      })
      const idProjetoApi = f.idProjeto > 0 ? f.idProjeto : 0
      const faturamentosPromise =
        idProjetoApi > 0
          ? faturamentoService.obterFaturamentosPorProjeto(idProjetoApi)
          : Promise.resolve([] as FaturamentoDto[])
      void Promise.all([faturamentoService.obterDetalhes(f.idFaturamento), faturamentosPromise])
        .then(([detalhes, faturamentosProjeto]) => {
          cacheDetalhesRef.current.set(f.idFaturamento, { detalhes, faturamentosProjeto })
          setPainelDetalhes((prev) => {
            if (!prev || prev.faturamentoLinha.idFaturamento !== f.idFaturamento) return prev
            return {
              ...prev,
              detalhes,
              faturamentosProjeto,
              carregando: false,
              erro: null,
            }
          })
        }, (err: Error) => {
          setPainelDetalhes((prev) => {
            if (!prev || prev.faturamentoLinha.idFaturamento !== f.idFaturamento) return prev
            return {
              ...prev,
              carregando: false,
              erro: err?.message || 'Erro ao carregar detalhes',
            }
          })
        })
    },
    [toast],
  )

  const totalValor = useMemo(() => {
    return faturamentos.reduce((acc, f) => acc + (Number(f.valorTotal) || 0), 0)
  }, [faturamentos])

  const totalHoras = useMemo(() => {
    return faturamentos.reduce((acc, f) => acc + (Number((f as any).totalHoras ?? f.totalHoras) || 0), 0)
  }, [faturamentos])

  const gruposPorTime = useMemo(() => {
    if (!isAdmin || faturamentos.length === 0) return []
    const map = new Map<number | string, FaturamentoDto[]>()
    for (const f of faturamentos) {
      const key = f.idTime ?? 'sem-time'
      const list = map.get(key) ?? []
      list.push(f)
      map.set(key, list)
    }
    return Array.from(map.entries())
      .map(([key, items]) => {
        const primeiro = items[0]
        const nomeTime = key === 'sem-time' ? 'Sem time' : (primeiro?.nomeTime ?? 'Sem time')
        const subTotalHoras = items.reduce((acc, i) => acc + (Number((i as any).totalHoras ?? i.totalHoras) || 0), 0)
        const subTotalValor = items.reduce((acc, i) => acc + (Number(i.valorTotal) || 0), 0)
        return { key, nomeTime, items, subTotalHoras, subTotalValor }
      })
      .sort((a, b) => (a.key === 'sem-time' ? 1 : b.key === 'sem-time' ? -1 : String(a.nomeTime).localeCompare(String(b.nomeTime))))
  }, [isAdmin, faturamentos])

  const quinzenaAtual = useMemo(() => getQuinzenaInicioFim(new Date()), [])

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    getValues,
  } = useForm<AcompanhamentoFilters>({
    resolver: zodResolver(acompanhamentoSchema),
    mode: 'onSubmit',
    defaultValues: {
      dataInicio: quinzenaAtual.inicio,
      dataFim: quinzenaAtual.fim,
      nomeProjeto: '',
      numeroTicket: '',
      statusCodigo: '',
      analistaSMNId: '',
      idTime: '',
    },
  })

  const dataInicioAtual = watch('dataInicio')
  const dataFimAtual = watch('dataFim')

  useEffect(() => {
    faturamentoService.obterStatusFaturamento()
      .then(setStatusFaturamento)
      .catch((error: any) => {
        toast.error('Erro', error?.message || 'Erro ao carregar status de faturamento')
      })
    faturamentoService.obterEtapasFaturamento()
      .then(setEtapasFaturamento)
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!isAdmin) return
    faturamentoService.obterAnalistasSMN()
      .then(setAnalistasSMN)
      .catch(() => {})
    timeService.listarTimes()
      .then(setTimes)
      .catch(() => {})
  }, [isAdmin])

  const executarBusca = (data: AcompanhamentoFilters) => {
    setBuscaRealizada(true)
    setIsSearching(true)
    startLoading('Buscando faturamentos...')

    const dataFimFinal = data.dataFim || quinzenaAtual.fim

    const filtros: BuscarAcompanhamentoFaturamentoDto = {
      dataInicio: data.dataInicio,
      dataFim: dataFimFinal,
      nomeProjeto: data.nomeProjeto?.trim() || undefined,
      numeroTicket: data.numeroTicket?.trim() || undefined,
      statusCodigo: data.statusCodigo ? Number(data.statusCodigo) : undefined,
      analistaSMNId: data.analistaSMNId ? Number(data.analistaSMNId) : undefined,
      idTime: data.idTime ? Number(data.idTime) : undefined,
    }

    faturamentoService.buscarAcompanhamento(filtros)
      .then((resultado) => {
        setFaturamentos(resultado)
      })
      .catch((error: any) => {
        toast.error('Erro', error?.message || 'Erro ao buscar faturamentos')
      })
      .finally(() => {
        setIsSearching(false)
        stopLoading()
      })
  }

  useEffect(() => {
    if (statusFaturamento.length === 0 || cargaInicialFeita.current) return
    cargaInicialFeita.current = true
    setValue('dataInicio', quinzenaAtual.inicio)
    setValue('dataFim', quinzenaAtual.fim)
    executarBusca({
      ...getValues(),
      dataInicio: quinzenaAtual.inicio,
      dataFim: quinzenaAtual.fim,
    })
  }, [statusFaturamento.length])

  const onSubmitForm = (data: AcompanhamentoFilters) => {
    executarBusca(data)
  }

  const limparFiltros = () => {
    const q = getQuinzenaInicioFim(new Date())
    reset({
      dataInicio: q.inicio,
      dataFim: q.fim,
      nomeProjeto: '',
      numeroTicket: '',
      statusCodigo: '',
      analistaSMNId: '',
      idTime: '',
    })
    setFaturamentos([])
    setBuscaRealizada(false)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-r from-brand-500 to-brand-600 rounded-lg flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Acompanhamento</h1>
            <p className="text-sm text-gray-500">Acompanhe os faturamentos por período, ticket, projeto e status. Período inicial: quinzena (01 a 15 e 16 ao último dia do mês).</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmitForm)} className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-gradient-to-r from-brand-500 to-brand-600 rounded-lg flex items-center justify-center">
            <Search className="w-4 h-4 text-white" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Filtros</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Início <span className="text-red-500">*</span>
            </label>
            <InputDate register={register('dataInicio')} error={errors.dataInicio} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Fim
            </label>
            <InputDate register={register('dataFim')} error={errors.dataFim} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ticket
            </label>
            <input
              {...register('numeroTicket')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
              placeholder="Ex: 12345"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Projeto
            </label>
            <input
              {...register('nomeProjeto')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
              placeholder="Nome do projeto"
            />
          </div>
        </div>

        {isAdmin && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time
              </label>
              <select
                {...register('idTime')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
              >
                <option value="">Todos</option>
                {times.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.descricao}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Analista SMN
              </label>
              <select
                {...register('analistaSMNId')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
              >
                <option value="">Todos</option>
                {analistasSMN.map((a) => (
                  <option key={a.pessoaId} value={a.pessoaId}>
                    {a.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="mt-4 max-w-xs">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            {...register('statusCodigo')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
          >
            <option value="">Todos</option>
            {statusFaturamento.map((s) => (
              <option key={s.codigo} value={s.codigo}>
                {s.descricao}
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={limparFiltros}
            className="px-3 py-2 text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200 font-medium"
          >
            <X className="w-4 h-4 mr-2 inline" />
            Limpar
          </button>
          <button
            type="submit"
            disabled={isSearching}
            className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Search className="w-4 h-4 mr-2 inline" />
            Buscar
          </button>
        </div>
      </form>

      <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Faturamentos</h2>
            <p className="text-sm text-gray-500">
              {buscaRealizada
                ? `${faturamentos.length} registro(s) encontrado(s)`
                : isSearching
                  ? 'Carregando...'
                  : 'Faça uma busca para visualizar os resultados'}
            </p>
          </div>
          {buscaRealizada && (
            <div className="text-right">
              <p className="text-xs text-gray-500">Período por Dt. Envio Analista</p>
              <p className="text-sm font-medium text-gray-800">
                {formatDateToBrazilian(dataInicioAtual)}{dataFimAtual ? ` a ${formatDateToBrazilian(dataFimAtual)}` : ''}
              </p>
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left text-sm font-medium text-gray-700 py-3 px-4 border-b">Ticket</th>
                <th className="text-left text-sm font-medium text-gray-700 py-3 px-4 border-b">Projeto</th>
                <th className="text-left text-sm font-medium text-gray-700 py-3 px-4 border-b">Responsável</th>
                <th className="text-left text-sm font-medium text-gray-700 py-3 px-4 border-b">Status</th>
                <th className="text-left text-sm font-medium text-gray-700 py-3 px-4 border-b">Dt. Envio Analista</th>
                <th className="text-left text-sm font-medium text-gray-700 py-3 px-4 border-b">Dt. Envio Momentum</th>
                <th className="text-right text-sm font-medium text-gray-700 py-3 px-4 border-b">Horas</th>
                <th className="text-right text-sm font-medium text-gray-700 py-3 px-4 border-b">Valor</th>
                <th className="text-center text-sm font-medium text-gray-700 py-3 px-2 border-b w-14"> </th>
              </tr>
            </thead>
            <tbody>
              {isAdmin && gruposPorTime.length > 0 ? (
                gruposPorTime.map((grupo) => (
                  <Fragment key={String(grupo.key)}>
                    <tr className="bg-indigo-100 border-b-2 border-indigo-200">
                      <td colSpan={9} className="py-2.5 px-4 text-sm font-semibold text-indigo-900">
                        Time: {grupo.nomeTime}
                      </td>
                    </tr>
                    {grupo.items.map((f, index) => (
                      <tr
                        key={`acompanh-grupo-${String(grupo.key)}-${index}-${f.idFaturamento}-${f.idTime ?? 'sem'}-${f.idLoteGeracaoSuporte ?? 'nolote'}`}
                        className={index % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'}
                      >
                        <td className="py-3 px-4 border-b border-gray-100 text-sm text-gray-800">{f.projetoNumeroTicket || '-'}</td>
                        <td className="py-3 px-4 border-b border-gray-100 text-sm text-gray-800">{f.projetoNome || '-'}</td>
                        <td className="py-3 px-4 border-b border-gray-100 text-sm text-gray-800">{responsavelAcompanhamentoExibicao(f)}</td>
                        <td className="py-3 px-4 border-b border-gray-100">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeClass(f.statusCodigo)}`}>
                            {f.statusDescricao || '-'}
                          </span>
                          {(() => {
                            const etapa = etapasFaturamento.find(e => e.codigoEtapaFaturamento === f.codigoEtapaFaturamento)
                            return etapa ? (
                              <p className="text-xs text-gray-500 mt-0.5">{etapa.descricao} ({etapa.percentual}%)</p>
                            ) : null
                          })()}
                        </td>
                        <td className="py-3 px-4 border-b border-gray-100 text-sm text-gray-800">{formatDateToBrazilian(f.dataEnvioAnalista)}</td>
                        <td className="py-3 px-4 border-b border-gray-100 text-sm text-gray-800">{formatDateToBrazilian(f.dataEnvioMomentum)}</td>
                        <td className="py-3 px-4 border-b border-gray-100 text-sm text-gray-800 text-right">{formatHoras((f as any).totalHoras ?? f.totalHoras)}</td>
                        <td className="py-3 px-4 border-b border-gray-100 text-sm text-gray-900 text-right font-medium">{formatCurrency(Number(f.valorTotal) || 0)}</td>
                        <td className="py-3 px-2 border-b border-gray-100 text-center">
                          <button
                            type="button"
                            disabled={!detalhesFaturamentoDisponivel(f)}
                            onClick={() => abrirPainelDetalhes(f)}
                            className="inline-flex p-2 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-gray-500 hover:text-brand-600 hover:bg-brand-50 enabled:hover:bg-brand-50"
                            title={
                              detalhesFaturamentoDisponivel(f)
                                ? 'Detalhes do faturamento'
                                : 'Faturamento ainda não vinculado; detalhes após o registro de faturamento'
                            }
                            aria-label="Detalhes do faturamento"
                          >
                            <Search className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-gray-100 font-medium border-b-2 border-gray-300">
                      <td className="py-2 px-4 text-sm text-gray-800" colSpan={6}>
                        Resumo do time
                      </td>
                      <td className="py-2 px-4 text-sm text-gray-900 text-right">
                        {formatHoras(grupo.subTotalHoras)}
                      </td>
                      <td className="py-2 px-4 text-sm text-gray-900 text-right">
                        {formatCurrency(grupo.subTotalValor)}
                      </td>
                      <td className="py-2 px-2 border-b border-gray-200" />
                    </tr>
                  </Fragment>
                ))
              ) : (
                faturamentos.map((f, index) => (
                  <tr
                    key={`acompanh-${index}-${f.idFaturamento}-${f.idTime ?? 'sem'}-${f.idLoteGeracaoSuporte ?? 'nolote'}`}
                    className={index % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'}
                  >
                    <td className="py-3 px-4 border-b border-gray-100 text-sm text-gray-800">{f.projetoNumeroTicket || '-'}</td>
                    <td className="py-3 px-4 border-b border-gray-100 text-sm text-gray-800">{f.projetoNome || '-'}</td>
                    <td className="py-3 px-4 border-b border-gray-100 text-sm text-gray-800">{responsavelAcompanhamentoExibicao(f)}</td>
                    <td className="py-3 px-4 border-b border-gray-100">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeClass(f.statusCodigo)}`}>
                        {f.statusDescricao || '-'}
                      </span>
                      {(() => {
                        const etapa = etapasFaturamento.find(e => e.codigoEtapaFaturamento === f.codigoEtapaFaturamento)
                        return etapa ? (
                          <p className="text-xs text-gray-500 mt-0.5">{etapa.descricao} ({etapa.percentual}%)</p>
                        ) : null
                      })()}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-100 text-sm text-gray-800">{formatDateToBrazilian(f.dataEnvioAnalista)}</td>
                    <td className="py-3 px-4 border-b border-gray-100 text-sm text-gray-800">{formatDateToBrazilian(f.dataEnvioMomentum)}</td>
                    <td className="py-3 px-4 border-b border-gray-100 text-sm text-gray-800 text-right">{formatHoras((f as any).totalHoras ?? f.totalHoras)}</td>
                    <td className="py-3 px-4 border-b border-gray-100 text-sm text-gray-900 text-right font-medium">{formatCurrency(Number(f.valorTotal) || 0)}</td>
                    <td className="py-3 px-2 border-b border-gray-100 text-center">
                      <button
                        type="button"
                        disabled={!detalhesFaturamentoDisponivel(f)}
                        onClick={() => abrirPainelDetalhes(f)}
                        className="inline-flex p-2 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-gray-500 hover:text-brand-600 hover:bg-brand-50 enabled:hover:bg-brand-50"
                        title={
                          detalhesFaturamentoDisponivel(f)
                            ? 'Detalhes do faturamento'
                            : 'Faturamento ainda não vinculado; detalhes após o registro de faturamento'
                        }
                        aria-label="Detalhes do faturamento"
                      >
                        <Search className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}

              {buscaRealizada && faturamentos.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-10 px-4 text-center text-sm text-gray-500">
                    Nenhum faturamento encontrado para os filtros informados.
                  </td>
                </tr>
              )}
            </tbody>
            {buscaRealizada && faturamentos.length > 0 && (
              <tfoot>
                <tr className="bg-gray-200 font-semibold">
                  <td className="py-3 px-4 border-t-2 border-gray-300 text-sm text-gray-900" colSpan={6}>
                    Resumo geral
                  </td>
                  <td className="py-3 px-4 border-t-2 border-gray-300 text-sm text-gray-900 text-right">
                    {formatHoras(totalHoras)}
                  </td>
                  <td className="py-3 px-4 border-t-2 border-gray-300 text-sm text-gray-900 text-right">
                    {formatCurrency(totalValor)}
                  </td>
                  <td className="py-3 px-2 border-t-2 border-gray-300" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {painelDetalhes &&
        createPortal(
          <div
            role="dialog"
            aria-label="Detalhes do faturamento"
            aria-modal="true"
            className="fixed z-[200] left-1/2 top-2 w-[min(56rem,calc(100vw-1.5rem))] max-h-[calc(100dvh-1rem)] -translate-x-1/2 flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-theme-sm"
          >
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
              <p className="text-sm font-semibold text-gray-900">Detalhes do faturamento</p>
              <button
                type="button"
                onClick={fecharPainelDetalhes}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-200 transition-colors"
                title="Fechar"
                aria-label="Fechar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="overflow-y-auto overflow-x-hidden flex-1 min-h-0">
            {painelDetalhes.erro && (
              <div className="p-4 text-sm text-red-600">{painelDetalhes.erro}</div>
            )}
            {!painelDetalhes.erro && painelDetalhes.carregando && !painelDetalhes.detalhes && (
              <div className="p-6 text-sm text-gray-600">Carregando detalhes…</div>
            )}
            {!painelDetalhes.erro && painelDetalhes.detalhes && painelDetalhes.faturamentosProjeto && (
              <FaturamentoDetalhesConteudo
                detalhesFaturamento={painelDetalhes.detalhes}
                faturamentoSelecionado={painelDetalhes.faturamentoLinha}
                etapasFaturamento={etapasFaturamento}
                faturamentosProjeto={painelDetalhes.faturamentosProjeto}
                detalhesFiltradosPorLinhaAcompanhamento
              />
            )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}
